import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { OTPRateLimitError, useOTPSender } from '@/features/auth/otp';

import AuthBranding from '../components/AuthBranding';
import OTPForm from '../components/OTPForm';
import SignInForm from '../components/SignInForm';

type AuthStep = 'sign-in' | 'otp';

export default function AuthenticationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { sendOTP } = useOTPSender();

  const rawNext = searchParams.get('next');
  // Only allow same-origin relative paths (must start with / but not // which is protocol-relative)
  const nextPath = rawNext && /^\/[^/]/.test(rawNext) ? rawNext : null;
  const handleAuthSuccess = nextPath
    ? () => {
        window.location.href = nextPath;
      }
    : undefined;
  const [step, setStep] = useState<AuthStep>('sign-in');
  const [otpData, setOtpData] = useState<{
    identifier: string;
    isEmail: boolean;
    hasExistingOTP?: boolean;
    timeLeft?: number;
  } | null>(null);

  const handleSignInSuccess = (data: {
    identifier: string;
    isEmail: boolean;
    hasExistingOTP?: boolean;
    timeLeft?: number;
  }) => {
    setOtpData(data);
    setStep('otp');
  };

  const handleResendOTP = async () => {
    if (!otpData) return;

    try {
      // Send OTP (enforces rate limiting)
      const result = await sendOTP({
        identifier: otpData.identifier,
        isEmail: otpData.isEmail,
        checkExisting: true,
        enforceRateLimit: true,
      });

      // Update OTP data with new values
      setOtpData(prev =>
        prev
          ? {
              ...prev,
              hasExistingOTP: result.hasExistingOtp,
              timeLeft: result.timeLeft,
            }
          : null,
      );
    } catch (error) {
      if (error instanceof OTPRateLimitError) {
        toast.error(t('dashboard.auth.otp.rateLimitError', { seconds: error.waitTimeSeconds }), {
          duration: 4000,
        });
      } else {
        throw error;
      }
    }
  };

  const handleBackFromOTP = () => {
    setOtpData(null);
    setStep('sign-in');
  };

  const handleUserNotFound = (identifier: string, isEmail: boolean) => {
    toast.info(
      isEmail
        ? t('dashboard.auth.signIn.userNotFoundEmail', { email: identifier })
        : t('dashboard.auth.signIn.userNotFoundPhone', { phoneNumber: identifier }),
      { duration: 4000 },
    );
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Auth forms */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {step === 'sign-in' && (
            <SignInForm onSuccess={handleSignInSuccess} onUserNotFound={handleUserNotFound} />
          )}
          {step === 'otp' && otpData ? (
            <OTPForm
              identifier={otpData.identifier}
              isEmail={otpData.isEmail}
              hasExistingOTP={otpData.hasExistingOTP}
              timeLeft={otpData.timeLeft}
              onResend={handleResendOTP}
              onBack={handleBackFromOTP}
              onSuccess={handleAuthSuccess}
            />
          ) : null}
        </div>
      </div>

      <AuthBranding />
    </div>
  );
}
